
import React from 'react';
import { Check, X, Info } from 'lucide-react';

const features = [
  { 
    name: "Setup Time", 
    diy: "3-6 months of YouTube tutorial hell, rebuilding things twice, and guessing.", 
    va: "2-4 weeks training someone who's never climbed a tree or seen a chipper.", 
    other: "1-2 weeks IF tech-savvy. Most give up and use 10% of the features.", 
    arbor: "14 Days Done-With-You (Milestone-based setup)" 
  },
  { 
    name: "Follow-up Speed", 
    diy: "2-3 days later when you're finally out of the tree and showered.", 
    va: "Business hours only—leads at 6pm Saturday wait until Monday 9am.", 
    other: "Manual—you still have to write and send every text yourself.", 
    arbor: "30 Seconds, AI-personalized, 24/7/365 availability" 
  },
  { 
    name: "Industry Knowledge", 
    diy: "You know trees, but your software thinks you sell software or cupcakes.", 
    va: "They'll call a crane a 'big truck thing' to your high-ticket customers.", 
    other: "Generic service software. No tree templates, no industry lingo.", 
    arbor: "Built by a Climber. Mentions rigging, species, bucket access, and ground prep." 
  },
  { 
    name: "Seasonal Marketing", 
    diy: "Wing it. Pray for storm season. Scramble every spring for leads.", 
    va: "They don't know tree seasons. You have to teach them quarterly strategy.", 
    other: "No seasonal automation. All campaign adjustments are manual.", 
    arbor: "Pre-built: Spring Pruning, Storm Prep, and Winter Quote campaigns." 
  },
  { 
    name: "True Monthly Cost", 
    diy: "$0/mo + 15 hrs/wk = ~$3,900/mo in lost billable field hours.", 
    va: "$1,800-2,500/mo + training + constant turnover headaches.", 
    other: "$400-800/mo WITHOUT the texting, automation, or AI features included.", 
    arbor: "$297/mo — less than the cost of ONE single tree removal job." 
  },
  { 
    name: "Works From The Truck", 
    diy: "Fumbling with spreadsheets on a sawdust-covered phone screen.", 
    va: "They're at a desk. You text them updates from the field like a middle-man.", 
    other: "Clunky mobile app built for office workers. Needs high-speed WiFi.", 
    arbor: "Big buttons. Designed for Carhartts and sweat, not suits and coffee shops." 
  },
  { 
    name: "Quote Follow-up", 
    diy: "Text when you remember. Statistics show 60% of quotes never get followed up.", 
    va: "One email. If they don't respond, the lead just dies in the inbox.", 
    other: "Sends the estimate link, then stops. No automatic nurture sequence.", 
    arbor: "7-touch sequence over 14 days. Email, SMS, AI calls. 58% avg conversion." 
  },
  { 
    name: "Storm Response", 
    diy: "Total chaos. Phone blows up. Manually text 100+ people while rigging.", 
    va: "Overwhelmed. Can't keep up with the volume. Miss half the high-ticket leads.", 
    other: "No storm workflows. You're winging it under massive pressure.", 
    arbor: "Storm Mode: AI triages by urgency + value. You focus on the high-ticket removals." 
  },
  { 
    name: "Review Generation", 
    diy: "Ask sometimes. Forget when you're slammed. Maybe 1 in 10 jobs get a review.", 
    va: "Generic 'please review us' emails that get ignored 95% of the time.", 
    other: "Basic feature. Not automated. Not personalized to the tree service experience.", 
    arbor: "Auto-sends 24hrs after job. 68% response rate. Dominates local Google Map pack." 
  },
  { 
    name: "Objection Handling", 
    diy: "Good on the phone, but suck via text when you're in a rush.", 
    va: "They don't know tree objections. 'Too expensive' stumps them every time.", 
    other: "No objection handling. Just a simple digital quoting tool.", 
    arbor: "Pre-loaded sales scripts for 'too expensive' and 'shopping around' objections." 
  },
  { 
    name: "Learning Curve", 
    diy: "Brutal. Expensive mistakes. You don't know what you don't know.", 
    va: "You're the teacher. Constant back-and-forth. Lots of guessing.", 
    other: "Designed for ANY service biz = bloated features you don't need. Confusing.", 
    arbor: "We build it WITH you on Zoom. Weekly coaching. You are never alone." 
  },
  { 
    name: "Business Metrics", 
    diy: "Spreadsheet if you're organized. Mental math if you're being honest.", 
    va: "They send reports you don't have time to look at or understand.", 
    other: "The data exists. Good luck making sense of it without a degree.", 
    arbor: "Dashboard: leads, quote-to-close %, pipeline value. Reviewed monthly WITH you." 
  },
  { 
    name: "Quote-to-Close Speed", 
    diy: "7-14 days. Lots of back-and-forth. Leads go stone cold.", 
    va: "5-10 days. They are slow and don't know which questions actually matter.", 
    other: "Depends on YOU manually following up (spoiler: it won't happen consistently).", 
    arbor: "2-4 days average. Books your calendar instantly. Frictionless." 
  },
  { 
    name: "Scalability", 
    diy: "Breaks when you hire crew #2. Totally unscalable manual mess.", 
    va: "Hire another VA. Now you're managing people instead of trees.", 
    other: "Scales but gets expensive fast. Multi-crew jumps to $1,200+/mo.", 
    arbor: "Built for Solo → 5 Crews. Same price. Coaching helps you scale without chaos." 
  },
  { 
    name: "Support/Coaching", 
    diy: "Google, prayer, and Facebook groups.", 
    va: "YOU have to support THEM. It's backwards.", 
    other: "Ticket system. 48hr response. Outsourced to people who've never seen a stump.", 
    arbor: "Direct team access. Weekly group coaching. Monthly 1-on-1 accountability." 
  }
];

const ComparisonChart: React.FC = () => {
  return (
    <section className="py-24 bg-white text-[#0F1419]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl md:text-7xl font-black mb-16 text-center uppercase italic tracking-tighter leading-none">
          How Do You Want To Scale?
        </h2>
        
        <div className="overflow-x-auto rounded-[2rem] shadow-2xl border-4 border-gray-100">
          <table className="w-full border-separate border-spacing-0 min-w-[1000px]">
            <thead>
              <tr className="bg-[#F4E4C1]/50">
                <th className="py-10 px-6 text-left font-black uppercase text-xs tracking-widest text-[#4E3629] border-b-4 border-[#4E3629]">Capability</th>
                <th className="py-10 px-6 text-center font-black uppercase text-xs tracking-widest text-gray-400 border-b-4 border-gray-100">Do It Yourself</th>
                <th className="py-10 px-6 text-center font-black uppercase text-xs tracking-widest text-gray-400 border-b-4 border-gray-100">Hire a VA</th>
                <th className="py-10 px-6 text-center font-black uppercase text-xs tracking-widest text-gray-400 border-b-4 border-gray-100">Other CRMs</th>
                <th className="py-10 px-6 text-center font-black uppercase text-xs tracking-widest text-white bg-[#1A4D2E] border-b-4 border-[#FF6B35]">Arbor Advantage</th>
              </tr>
            </thead>
            <tbody className="text-sm font-black">
              {features.map((f, i) => (
                <tr key={i} className="group transition-colors">
                  <td className="py-8 px-6 border-b border-gray-100 bg-[#F4E4C1]/10 group-hover:bg-[#F4E4C1]/30 font-black italic">{f.name}</td>
                  <td className="py-8 px-6 border-b border-gray-100 text-center text-gray-400 font-bold opacity-60 italic">{f.diy}</td>
                  <td className="py-8 px-6 border-b border-gray-100 text-center text-gray-400 font-bold opacity-60 italic">{f.va}</td>
                  <td className="py-8 px-6 border-b border-gray-100 text-center text-gray-400 font-bold opacity-60 italic">{f.other}</td>
                  <td className="py-8 px-6 border-b border-gray-100 text-center bg-[#1A4D2E]/5 text-[#1A4D2E] text-base uppercase italic leading-tight">{f.arbor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-8 text-center">
          <p className="text-gray-400 font-bold italic">* Based on average field data from 200+ tree service partners.</p>
        </div>
      </div>
    </section>
  );
};

export default ComparisonChart;
